import {
    VOLUME_PROFILE_CHART_BARS,
    VOLUME_PROFILE_CURRENT_PRICE,
    VOLUME_PROFILE_LEVELS,
    VOLUME_PROFILE_VWAP,
} from './fixtures';
import { setRange, setSession, setTab, setViewMode, toggleOverlay } from './state/actions';
import { reducer } from './state/reducer';
import { createInitialVolumeProfileState } from './state/types';
import type {
    VolumeProfileChartBar,
    VolumeProfileLevel,
    VolumeProfileLegendItem,
    VolumeProfileLevelView,
    VolumeProfileOverlay,
    VolumeProfileRange,
    VolumeProfileSession,
    VolumeProfileStatGroup,
    VolumeProfileState,
    VolumeProfileTabId,
    VolumeProfileViewMode,
} from './state/types';
import { computed, signal } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import numbro from 'numbro';

const EMPTY_PROFILE_ROWS: readonly VolumeProfileLevelView[] = Object.freeze([]);
const EMPTY_STATS: readonly VolumeProfileStatGroup[] = Object.freeze([]);

function totalVolume(level: VolumeProfileLevel): number {
    return level.bid + level.mid + level.ask;
}

function sessionVolume(level: VolumeProfileLevel, session: VolumeProfileSession): number {
    switch (session) {
        case 'all':
            return totalVolume(level);
        case 'premarket':
            return level.premarket;
        case 'regular':
            return level.regular;
        case 'postmarket':
            return level.postmarket;
        default: {
            const _exhaustiveCheck: never = session;
            return 0;
        }
    }
}

function scaledQuote(
    level: VolumeProfileLevel,
    session: VolumeProfileSession,
): Pick<VolumeProfileLevel, 'bid' | 'mid' | 'ask'> {
    if (session === 'all') return level;
    const total = totalVolume(level);
    if (total === 0) return { bid: 0, mid: 0, ask: 0 };
    const scale = sessionVolume(level, session) / total;
    return {
        bid: level.bid * scale,
        mid: level.mid * scale,
        ask: level.ask * scale,
    };
}

function formatPrice(value: number): string {
    return numbro(value).formatCurrency({ currencySymbol: '$', mantissa: 2 });
}

function formatVolume(value: number): string {
    return numbro(value).format({ average: true, mantissa: 1 }).toUpperCase();
}

function formatPercent(value: number): string {
    return numbro(value).format({ output: 'percent', mantissa: 1 });
}

function progress(value: number, base: number): number {
    return base > 0 ? Math.min(100, Math.max(0, (value / base) * 100)) : 0;
}

function toneClass(tone: VolumeProfileStatGroup['rows'][number]['tone']): string {
    switch (tone) {
        case 'aqua':
            return 'bg-aqua-500';
        case 'jade':
            return 'bg-jade-450';
        case 'ember':
            return 'bg-ember-450';
        case 'honey':
            return 'bg-honey-450';
        case 'purple':
            return 'bg-purple-500';
        case 'default':
            return 'bg-gray-400';
        default: {
            const _exhaustiveCheck: never = tone;
            return 'bg-gray-400';
        }
    }
}

function aggregate(levels: readonly VolumeProfileLevel[], session: VolumeProfileSession) {
    const adjusted = levels.map((level) => ({
        level,
        quote: scaledQuote(level, session),
        sessionTotal: sessionVolume(level, session),
    }));
    const maxTotal = Math.max(...adjusted.map((x) => x.sessionTotal), 1);
    const poc = adjusted.reduce((best, next) => (next.sessionTotal > best.sessionTotal ? next : best), adjusted[0]);
    const sortedAsc = [...adjusted].sort((a, b) => a.level.price - b.level.price);
    const valueAreaLevels = sortedAsc.slice(2, 8);
    const val = valueAreaLevels[0]?.level.price ?? sortedAsc[0]?.level.price ?? VOLUME_PROFILE_CURRENT_PRICE;
    const vah = valueAreaLevels[valueAreaLevels.length - 1]?.level.price ?? val;
    const totals = adjusted.reduce(
        (acc, x) => {
            acc.bid += x.quote.bid;
            acc.mid += x.quote.mid;
            acc.ask += x.quote.ask;
            acc.sessionTotal += x.sessionTotal;
            acc.premarket += x.level.premarket;
            acc.regular += x.level.regular;
            acc.postmarket += x.level.postmarket;
            return acc;
        },
        { bid: 0, mid: 0, ask: 0, sessionTotal: 0, premarket: 0, regular: 0, postmarket: 0 },
    );
    return {
        adjusted,
        maxTotal,
        pocPrice: poc?.level.price ?? VOLUME_PROFILE_CURRENT_PRICE,
        vah,
        val,
        totals,
    };
}

function buildSegments(
    viewMode: VolumeProfileViewMode,
    level: VolumeProfileLevel,
    quote: Pick<VolumeProfileLevel, 'bid' | 'mid' | 'ask'>,
    maxTotal: number,
) {
    if (viewMode === 'quote') {
        return [
            { id: 'bid', label: 'Bid', widthPct: progress(quote.bid, maxTotal), className: 'bg-ember-450/60' },
            { id: 'mid', label: 'Mid', widthPct: progress(quote.mid, maxTotal), className: 'bg-purple-500/55' },
            { id: 'ask', label: 'Ask', widthPct: progress(quote.ask, maxTotal), className: 'bg-jade-450/60' },
        ];
    }
    if (viewMode === 'session') {
        return [
            {
                id: 'premarket',
                label: 'Pre',
                widthPct: progress(level.premarket, maxTotal),
                className: 'bg-honey-450/65',
            },
            { id: 'regular', label: 'Reg', widthPct: progress(level.regular, maxTotal), className: 'bg-aqua-500/60' },
            {
                id: 'postmarket',
                label: 'Post',
                widthPct: progress(level.postmarket, maxTotal),
                className: 'bg-purple-500/55',
            },
        ];
    }
    return [
        {
            id: 'total',
            label: 'Total',
            widthPct: progress(totalVolume(level), maxTotal),
            className: 'bg-honey-450/65',
        },
    ];
}

function buildLegend(tabId: VolumeProfileTabId, viewMode: VolumeProfileViewMode): readonly VolumeProfileLegendItem[] {
    const base =
        tabId === 'chart'
            ? [
                  { id: 'bid', label: 'Bid', className: 'bg-ember-450/60' },
                  { id: 'ask', label: 'Ask', className: 'bg-jade-450/60' },
              ]
            : viewMode === 'quote'
              ? [
                    { id: 'bid', label: 'Bid', className: 'bg-ember-450/60' },
                    { id: 'mid', label: 'Mid', className: 'bg-purple-500/55' },
                    { id: 'ask', label: 'Ask', className: 'bg-jade-450/60' },
                ]
              : viewMode === 'session'
                ? [
                      { id: 'premarket', label: 'Pre', className: 'bg-honey-450/65' },
                      { id: 'regular', label: 'Reg', className: 'bg-aqua-500/60' },
                      { id: 'postmarket', label: 'Post', className: 'bg-purple-500/55' },
                  ]
                : [{ id: 'total', label: 'Total', className: 'bg-honey-450/65' }];
    return [
        ...base,
        { id: 'poc', label: 'POC', className: 'bg-honey-450' },
        { id: 'value-area', label: 'Value Area', className: 'bg-aqua-500/60' },
    ];
}

function buildStats(
    levels: readonly VolumeProfileLevel[],
    session: VolumeProfileSession,
): readonly VolumeProfileStatGroup[] {
    if (levels.length === 0) return EMPTY_STATS;
    const data = aggregate(levels, session);
    const total = data.totals.sessionTotal;
    const quoteTotal = data.totals.bid + data.totals.mid + data.totals.ask;
    const sessionTypeTotal = data.totals.premarket + data.totals.regular + data.totals.postmarket;
    const safeTotal = total || 1;
    const safeQuoteTotal = quoteTotal || 1;
    const safeSessionTypeTotal = sessionTypeTotal || 1;
    const delta = data.totals.ask - data.totals.bid;
    const openAuction = total * 0.072;
    const closeAuction = total * 0.083;

    const row = (
        label: string,
        value: string,
        tone: VolumeProfileStatGroup['rows'][number]['tone'] = 'default',
        pct: number | null = null,
    ) => ({
        label,
        value,
        tone,
        progressPct: pct,
        progressClassName: toneClass(tone),
    });

    return [
        {
            id: 'levels',
            title: 'Key Levels',
            rows: [
                row('POC', formatPrice(data.pocPrice), 'honey'),
                row('VAH', formatPrice(data.vah), 'aqua'),
                row('VAL', formatPrice(data.val), 'aqua'),
            ],
        },
        {
            id: 'quote',
            title: 'Quote Location',
            rows: [
                row('Vol Bid', formatVolume(data.totals.bid), 'ember', progress(data.totals.bid, safeQuoteTotal)),
                row('Vol Ask', formatVolume(data.totals.ask), 'jade', progress(data.totals.ask, safeQuoteTotal)),
                row('Vol Mid', formatVolume(data.totals.mid), 'purple', progress(data.totals.mid, safeQuoteTotal)),
                row('% Bid', formatPercent(data.totals.bid / safeQuoteTotal), 'ember'),
                row('% Ask', formatPercent(data.totals.ask / safeQuoteTotal), 'jade'),
                row('% Mid', formatPercent(data.totals.mid / safeQuoteTotal), 'purple'),
            ],
        },
        {
            id: 'session',
            title: 'Session Type',
            rows: [
                row(
                    'Vol Pre',
                    formatVolume(data.totals.premarket),
                    'honey',
                    progress(data.totals.premarket, safeSessionTypeTotal),
                ),
                row(
                    'Vol Reg',
                    formatVolume(data.totals.regular),
                    'aqua',
                    progress(data.totals.regular, safeSessionTypeTotal),
                ),
                row(
                    'Vol Post',
                    formatVolume(data.totals.postmarket),
                    'purple',
                    progress(data.totals.postmarket, safeSessionTypeTotal),
                ),
                row('% Pre', formatPercent(data.totals.premarket / safeSessionTypeTotal), 'honey'),
                row('% Reg', formatPercent(data.totals.regular / safeSessionTypeTotal), 'aqua'),
                row('% Post', formatPercent(data.totals.postmarket / safeSessionTypeTotal), 'purple'),
            ],
        },
        {
            id: 'auctions',
            title: 'Auctions',
            rows: [
                row('Open Auction', formatVolume(openAuction), 'honey', progress(openAuction, safeTotal)),
                row('Close Auction', formatVolume(closeAuction), 'aqua', progress(closeAuction, safeTotal)),
            ],
        },
        {
            id: 'totals',
            title: 'Totals',
            rows: [
                row('Total Vol', formatVolume(total), 'default'),
                row(
                    'Delta',
                    `${delta >= 0 ? '+' : '-'}${formatVolume(Math.abs(delta))}`,
                    delta >= 0 ? 'jade' : 'ember',
                ),
            ],
        },
    ];
}

function rangeSubtitle(range: VolumeProfileRange): string {
    switch (range) {
        case 'day':
            return 'Current day - full session';
        case '1h':
            return 'Last 60 minutes';
        case '5m':
            return 'Last 5 minutes';
        case '1m':
            return 'Last 1 minute';
        case 'custom':
            return 'Custom range - 09:30 to 16:00 ET';
        default: {
            const _exhaustiveCheck: never = range;
            return '';
        }
    }
}

export class VolumeProfileModel implements Disposable {
    readonly #state = signal<VolumeProfileState>(createInitialVolumeProfileState());
    readonly #loading = signal(false);
    readonly #error = signal<string | null>(null);

    readonly #tabId = computed(() => this.#state.value.tabId);
    readonly #viewMode = computed(() => this.#state.value.viewMode);
    readonly #session = computed(() => this.#state.value.session);
    readonly #range = computed(() => this.#state.value.range);
    readonly #overlays = computed(() => this.#state.value.overlays);
    readonly #levels: ReadonlySignal<readonly VolumeProfileLevelView[]>;
    readonly #stats: ReadonlySignal<readonly VolumeProfileStatGroup[]>;
    readonly #legend: ReadonlySignal<readonly VolumeProfileLegendItem[]>;
    readonly #rangeSubtitle = computed(() => rangeSubtitle(this.#state.value.range));
    readonly #isEmpty: ReadonlySignal<boolean>;
    readonly #chartBars = signal<readonly VolumeProfileChartBar[]>(VOLUME_PROFILE_CHART_BARS);
    readonly #currentPrice = signal(VOLUME_PROFILE_CURRENT_PRICE);
    readonly #vwap = signal(VOLUME_PROFILE_VWAP);

    constructor() {
        this.#levels = computed(() => {
            const data = aggregate(VOLUME_PROFILE_LEVELS, this.#state.value.session);
            if (data.adjusted.length === 0) return EMPTY_PROFILE_ROWS;
            return data.adjusted.map(({ level, quote, sessionTotal }) => ({
                id: level.id,
                price: level.price,
                priceFormatted: formatPrice(level.price),
                total: sessionTotal,
                totalFormatted: formatVolume(sessionTotal),
                segments: buildSegments(this.#state.value.viewMode, level, quote, data.maxTotal),
                isPoc: level.price === data.pocPrice,
                isVah: level.price === data.vah,
                isVal: level.price === data.val,
            }));
        });
        this.#stats = computed(() => buildStats(VOLUME_PROFILE_LEVELS, this.#state.value.session));
        this.#legend = computed(() => buildLegend(this.#state.value.tabId, this.#state.value.viewMode));
        this.#isEmpty = computed(() => this.#levels.value.length === 0);
    }

    get tabId(): ReadonlySignal<VolumeProfileTabId> {
        return this.#tabId;
    }

    get viewMode(): ReadonlySignal<VolumeProfileViewMode> {
        return this.#viewMode;
    }

    get session(): ReadonlySignal<VolumeProfileSession> {
        return this.#session;
    }

    get range(): ReadonlySignal<VolumeProfileRange> {
        return this.#range;
    }

    get overlays(): ReadonlySignal<Readonly<Record<VolumeProfileOverlay, boolean>>> {
        return this.#overlays;
    }

    get loading(): ReadonlySignal<boolean> {
        return this.#loading;
    }

    get error(): ReadonlySignal<string | null> {
        return this.#error;
    }

    get levels(): ReadonlySignal<readonly VolumeProfileLevelView[]> {
        return this.#levels;
    }

    get stats(): ReadonlySignal<readonly VolumeProfileStatGroup[]> {
        return this.#stats;
    }

    get legend(): ReadonlySignal<readonly VolumeProfileLegendItem[]> {
        return this.#legend;
    }

    get rangeSubtitle(): ReadonlySignal<string> {
        return this.#rangeSubtitle;
    }

    get isEmpty(): ReadonlySignal<boolean> {
        return this.#isEmpty;
    }

    get chartBars(): ReadonlySignal<readonly VolumeProfileChartBar[]> {
        return this.#chartBars;
    }

    get currentPrice(): ReadonlySignal<number> {
        return this.#currentPrice;
    }

    get vwap(): ReadonlySignal<number> {
        return this.#vwap;
    }

    setTab(tabId: VolumeProfileTabId): void {
        this.#state.value = reducer(this.#state.value, setTab(tabId));
    }

    setViewMode(viewMode: VolumeProfileViewMode): void {
        this.#state.value = reducer(this.#state.value, setViewMode(viewMode));
    }

    setSession(session: VolumeProfileSession): void {
        this.#state.value = reducer(this.#state.value, setSession(session));
    }

    setRange(range: VolumeProfileRange): void {
        this.#state.value = reducer(this.#state.value, setRange(range));
    }

    toggleOverlay(overlay: VolumeProfileOverlay): void {
        this.#state.value = reducer(this.#state.value, toggleOverlay(overlay));
    }

    refetch(): void {
        // Backend integration will invalidate/refetch the volume profile query here once available.
    }

    [Symbol.dispose](): void {
        // Local fixture-backed model has no external resources yet.
    }
}

export type {
    VolumeProfileChartBar,
    VolumeProfileLegendItem,
    VolumeProfileLevelView,
    VolumeProfileOverlay,
    VolumeProfileRange,
    VolumeProfileSession,
    VolumeProfileStatGroup,
    VolumeProfileTabId,
    VolumeProfileViewMode,
} from './state/types';
